// React-Hook-Wrapper um den trading-store. V1-Equivalent: src/hooks/useTradingLog.js
// API-Surface entspricht V1-Imports von App.js, TradingTab.js, TradingView.js,
// useBuildContext.js — siehe docs/audit für die Call-Sites.

import { useMemo } from 'react';
import {
  computeStats,
  filterClosed,
  filterOpen,
  formatTradesForPrompt,
} from '@/lib/trade-log';
import { useTradingStore } from '@/stores/trading-store';
import type { CloseTradeInput, CreateTradeInput, Trade, TradingStats } from '@/types/trade';

export interface UseTradingLogReturn {
  /** Alle Trades (offen + geschlossen) */
  trades: Trade[];
  /** Offene Copy-Trades */
  openTrades: Trade[];
  /** Geschlossene Trades */
  closedTrades: Trade[];
  /** Trading-Stats über alle geschlossenen Trades — `null` wenn keine */
  stats: TradingStats | null;
  /** Vorformatierter Text-Block für EVE-Prompt — `null` wenn keine Trades */
  formattedForPrompt: string | null;

  /** Neuer Copy-Trade. Gibt das erzeugte Trade-Objekt zurück. */
  createTrade: (input: CreateTradeInput) => Trade;
  /** Trade schließen. Gibt `true` zurück wenn er offen war (V1 `wasClosed`). */
  closeTrade: (id: string, input: CloseTradeInput) => boolean;
  /** Notes eines Trades aktualisieren. */
  updateNotes: (id: string, notes: string) => void;
  /** Trade hart löschen. */
  deleteTrade: (id: string) => void;
  /** Alle Trades löschen (V1 `clearTradingLog`). */
  clearAll: () => void;
}

/**
 * Liefert die komplette Trading-Log-API als React-Hook.
 *
 * Wir subscriben nur auf `trades` (referenziell stabil zwischen Store-Updates)
 * und derivieren openTrades/closedTrades/stats/formattedForPrompt via `useMemo`.
 * Damit vermeiden wir den Infinite-Loop, den ein selector mit `filter()`+`compute()`
 * erzeugen würde (jeder Call gäbe neue Array/Object-Referenzen zurück).
 */
export function useTradingLog(): UseTradingLogReturn {
  const trades = useTradingStore((s) => s.trades);

  const openTrades = useMemo(() => filterOpen(trades), [trades]);
  const closedTrades = useMemo(() => filterClosed(trades), [trades]);
  const stats = useMemo(() => computeStats(trades), [trades]);
  const formattedForPrompt = useMemo(() => formatTradesForPrompt(trades), [trades]);

  const createTrade = useTradingStore((s) => s.createTrade);
  const closeTrade = useTradingStore((s) => s.closeTrade);
  const updateNotes = useTradingStore((s) => s.updateNotes);
  const deleteTrade = useTradingStore((s) => s.deleteTrade);
  const clearAll = useTradingStore((s) => s.clearAll);

  return {
    trades,
    openTrades,
    closedTrades,
    stats,
    formattedForPrompt,
    createTrade,
    closeTrade,
    updateNotes,
    deleteTrade,
    clearAll,
  };
}
